// Estado global mínimo de la PWA: actualización disponible, evento de instalación y "trabajo en
// curso" (borradores sin guardar, compresión o cargas) que impide actualizar sin riesgo.
import { useSyncExternalStore } from 'react';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  updateAvailable: boolean;
  installPrompt: InstallPromptEvent | null;
  busyKeys: ReadonlySet<string>;
  online: boolean;
}

let state: PwaState = {
  updateAvailable: false,
  installPrompt: null,
  busyKeys: new Set(),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
};
const listeners = new Set<() => void>();

function set(patch: Partial<PwaState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function usePwaState(): PwaState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

export function setUpdateAvailable(value: boolean) {
  set({ updateAvailable: value });
}

/** Marca o desmarca una tarea que no debe interrumpirse con una recarga. */
export function setBusy(key: string, busy: boolean) {
  if (state.busyKeys.has(key) === busy) return;
  const next = new Set(state.busyKeys);
  if (busy) next.add(key);
  else next.delete(key);
  set({ busyKeys: next });
}

export function isBusy(): boolean {
  return state.busyKeys.size > 0;
}

export function takeInstallPrompt(): InstallPromptEvent | null {
  const prompt = state.installPrompt;
  set({ installPrompt: null });
  return prompt;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // la app ofrece el botón Instalar en su propio momento
    set({ installPrompt: event as InstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => set({ installPrompt: null }));
  window.addEventListener('online', () => set({ online: true }));
  window.addEventListener('offline', () => set({ online: false }));
}
